import React, { startTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import {
  activeTabAtom,
  sidebarCollapsedAtom
} from '../../../../atoms/v3/uiAtoms';
import { DesktopOutline } from './DesktopOutline';
import {
  UserIcon,
  UserIconFilled,
  TaskV2Icon,
  TaskV2IconFilled,
  ProjectIcon,
  ProjectIconFilled
} from '../ios/IOSIcons';

/**
 * 桌面侧边栏组件
 *
 * 实现了一个符合 iOS/iPadOS/macOS 设计规范的侧边栏：
 * - 顶部：导航标签
 * - 中间：分隔线
 * - 底部：大纲区域
 * - 可折叠为窄版
 */
export const DesktopSidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useAtom(activeTabAtom);
  const [sidebarCollapsed, setSidebarCollapsed] = useAtom(sidebarCollapsedAtom);
  const { t } = useTranslation(['v3/common', 'common']);

  // 标签配置 - 移除对话标签
  const tabs = [
    {
      id: 'taskv2',
      label: t('tabs.taskv2'),
      icon: <TaskV2Icon />,
      activeIcon: <TaskV2IconFilled />
    },
    {
      id: 'projectv2',
      label: t('tabs.projectv2'),
      icon: <ProjectIcon />,
      activeIcon: <ProjectIconFilled />
    },
    {
      id: 'me',
      label: t('tabs.me'),
      icon: <UserIcon />,
      activeIcon: <UserIconFilled />
    },
  ];

  // 处理标签点击
  const handleTabClick = (tabId: string) => {
    const defaultPath = `/v3/${tabId}`;

    // 只在路径不同时进行导航
    if (location.pathname !== defaultPath) {
      // 使用 startTransition 包装导航和状态更新，实现 optimistic UI
      console.log(`[DesktopSidebar] Tab clicked: ${tabId}, navigating to ${defaultPath}`);
      startTransition(() => {
        // 立即更新活动标签状态，不等待导航完成
        setActiveTab(tabId);
        console.log(`[DesktopSidebar] Active tab updated to: ${tabId}`);

        // 执行导航
        navigate(defaultPath);
        console.log(`[DesktopSidebar] Navigation executed to: ${defaultPath}`);
      });
    }
  };

  // 切换侧边栏折叠状态
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div
      className={`fixed top-0 left-0 h-full bg-ios-dark-gray1 dark:bg-ios-dark-gray6 border-r border-ios-gray5 dark:border-ios-dark-gray4 transition-all duration-300 ease-in-out z-10 ${
        sidebarCollapsed ? 'w-[4.375rem]' : 'w-[16.25rem]'
      }`}
    >
      {/* 折叠/展开按钮 */}
      <button
        onClick={toggleSidebar}
        className={`fixed top-4 ${sidebarCollapsed ? 'left-[4.375rem]' : 'left-[16.25rem]'} w-6 h-6 bg-white dark:bg-ios-dark-gray5 border border-ios-gray5 dark:border-ios-dark-gray4 rounded-r-md flex items-center justify-center text-ios-gray dark:text-ios-dark-gray hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 transition-colors z-40`}
        aria-label={sidebarCollapsed ? t('common:keymap.expandLeftSidebar') : t('common:keymap.collapseLeftSidebar')}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-4 h-4"
        >
          <polyline points={sidebarCollapsed ? "9 18 15 12 9 6" : "15 18 9 12 15 6"}></polyline>
        </svg>
      </button>

      {/* 导航标签 */}
      <div className="pt-4 pb-2">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`flex items-center w-full px-4 py-3 transition-colors ${
                isActive
                  ? 'bg-ios-blue/10 dark:bg-ios-dark-blue/20 text-ios-blue dark:text-ios-dark-blue'
                  : 'text-ios-gray dark:text-ios-dark-gray hover:bg-gray-200/50 dark:hover:bg-ios-dark-gray5/50 active:bg-gray-200/70 dark:active:bg-ios-dark-gray5/70'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
            >
              <div className="w-6 h-6">
                {isActive ? tab.activeIcon : tab.icon}
              </div>
              {!sidebarCollapsed && (
                <span className="ml-3 text-ios-body font-medium">
                  {tab.label}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 分隔线 */}
      <div className="mx-4 h-px bg-ios-gray5 dark:bg-ios-dark-gray4"></div>

      {/* 大纲区域 */}
      <div className="mt-2 overflow-y-auto" style={{ height: 'calc(100% - 15.625rem)' }}>
        <DesktopOutline />
      </div>
    </div>
  );
};
